import { Avatar, ChatBubble } from '@amzn/awsui-chat-components';
import { Button, ButtonGroup, SpaceBetween } from '@amzn/awsui-components-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { FeatureName, useFeatureUsageTracker } from '@/src/utils/engagement/engagement-tracker';

export const ConversationItemBubble: React.FC<{
  role: 'user' | 'assistant';
  ariaLabel: string;
  showLoadingBar?: boolean;
  messageId: string;
  triggerFeedbackModal: () => void;
  onThumbsUpDown: (action: string) => void;
}> = ({ role, ariaLabel, showLoadingBar, messageId, triggerFeedbackModal, onThumbsUpDown, children }) => {
  const { t } = useTranslation();
  const trackFeatureUsage = useFeatureUsageTracker();
  const [sentFeedback, setSentFeedback] = useState<string | undefined>();
  return (
    <>
      <ChatBubble
        avatar={
          <Avatar
            ariaLabel={
              role === 'assistant'
                ? t(i18nKeys.conversationalAI.ariaLabels.assistant)
                : t(i18nKeys.conversationalAI.ariaLabels.user)
            }
            color={role === 'assistant' ? 'gen-ai' : 'default'}
            iconName={role === 'assistant' ? 'gen-ai' : 'face-happy-filled'}
            imgUrl={role === 'assistant' ? '/assets/monkey-in-circle.png' : undefined}
          />
        }
        type={role === 'assistant' ? 'incoming' : 'outgoing'}
        showLoadingBar={!!showLoadingBar}
        ariaLabel={ariaLabel}
        actions={
          (role === 'assistant' && !showLoadingBar) || sentFeedback ? (
            <SpaceBetween direction="horizontal" size="xs">
              {role === 'assistant' && !showLoadingBar && (
                <ButtonGroup
                  variant="icon"
                  onItemClick={({ detail }) => {
                    setSentFeedback(detail.id);
                    trackFeatureUsage(FeatureName.AI_TEAMMATE, 'click', {
                      messageId,
                      actionCategory: detail.id,
                    });
                    onThumbsUpDown(detail.id);
                  }}
                  items={[
                    {
                      type: 'icon-button',
                      iconName: sentFeedback === 'thumbs-up' ? 'thumbs-up-filled' : 'thumbs-up',
                      id: 'thumbs-up',
                      text: t(i18nKeys.conversationalAI.feedback.thumbsUp),
                      disabled: !!sentFeedback,
                    },
                    {
                      type: 'icon-button',
                      iconName: sentFeedback === 'thumbs-down' ? 'thumbs-down-filled' : 'thumbs-down',
                      id: 'thumbs-down',
                      text: t(i18nKeys.conversationalAI.feedback.thumbsDown),
                      disabled: !!sentFeedback,
                    },
                  ]}
                />
              )}
              {sentFeedback && (
                <Button variant="inline-link" onClick={triggerFeedbackModal}>
                  {t(i18nKeys.conversationalAI.jammyFeedback.additionalFeedback)}
                </Button>
              )}
            </SpaceBetween>
          ) : undefined
        }
      >
        {children}
      </ChatBubble>
    </>
  );
};

export default ConversationItemBubble;
